import { ExpandedField } from './endpoint.interface';
import { Schema } from './api-spec.interface';
import {
  extractEnumSchema,
  generateSchemaExample,
  getSchemaDataType,
  isSingleEnumOneOf,
  unwrapSingleOneOf,
} from './schema-introspection.utils';

/**
 * Check if schema should be expanded
 * @param schema - The schema to check
 * @returns Whether schema should be expanded
 */
export function shouldExpandSchema(schema: Schema | undefined): boolean {
  if (!schema) return false;

  // Don't expand oneOf with single enum schema
  if (isSingleEnumOneOf(schema)) {
    return false;
  }

  // Expand arrays when they contain complex object items
  if (schema.type === 'array' && schema.items) {
    return shouldExpandSchema(schema.items);
  }

  return !!(
    schema.type === 'object' || schema.allOf || schema.properties || schema.oneOf || (schema.discriminator && schema.oneOf)
  );
}

/**
 * Expand a schema into individual fields
 * @param schema - The schema to expand
 * @param parentPath - Parent path for nested fields
 * @param depth - Current depth to prevent infinite recursion
 * @returns Array of expanded field definitions
 */
export function expandSchemaToFields(schema: Schema | undefined, parentPath = '', depth = 0): ExpandedField[] {
  const fields: ExpandedField[] = [];

  // Prevent deep recursion
  if (depth > 10) {
    console.warn('Max expansion depth reached');
    return fields;
  }

  if (!schema) {
    return fields;
  }

  // Handle arrays with object items on schema root
  if (schema.type === 'array' && schema.items && shouldExpandSchema(schema.items)) {
    return expandSchemaToFields(schema.items, parentPath ? `${parentPath}[0]` : '[0]', depth + 1);
  }

  // Handle allOf composition
  if (schema.allOf && Array.isArray(schema.allOf)) {
    schema.allOf.forEach((subSchema: Schema) => {
      fields.push(...expandSchemaToFields(subSchema, parentPath, depth + 1));
    });
    return dedupeExpandedFields(fields);
  }

  // Handle oneOf composition
  if (schema.oneOf && Array.isArray(schema.oneOf)) {
    if (isSingleEnumOneOf(schema)) {
      return fields;
    }

    schema.oneOf.forEach((subSchema: Schema) => {
      fields.push(...expandSchemaToFields(subSchema, parentPath, depth + 1));
    });
    return dedupeExpandedFields(fields);
  }

  // Handle object properties
  if (!schema.properties) {
    return fields;
  }

  Object.entries(schema.properties).forEach(([fieldName, fieldSchemaInput]: [string, Schema]) => {
    // Skip discriminator fields in docs UI
    if (fieldName === 'discriminator' || fieldName === 'Type') {
      return;
    }

    const fieldSchema = unwrapSingleOneOf(fieldSchemaInput);
    const fullFieldName = parentPath ? `${parentPath}.${fieldName}` : fieldName;
    const isRequired = schema.required?.includes(fieldName) === true;

    // Handle arrays with complex items
    if (fieldSchema.type === 'array' && fieldSchema.items && shouldExpandSchema(fieldSchema.items)) {
      const groupField: ExpandedField = {
        name: fullFieldName,
        displayName: fieldName,
        dataType: `array<${getSchemaDataType(fieldSchema.items)}>`,
        required: isRequired,
        description: fieldSchema.description,
        nullable: fieldSchema.nullable,
        isGroup: true,
        groupFields: expandSchemaToFields(fieldSchema.items, `${fullFieldName}[0]`, depth + 1),
      };
      fields.push(groupField);
      return;
    }

    // Create groups for complex objects and composed schemas
    if (shouldCreateGroup(fieldSchema)) {
      const groupField: ExpandedField = {
        name: fullFieldName,
        displayName: fieldName,
        dataType: getSchemaDataType(fieldSchema),
        required: isRequired,
        description: fieldSchema.description,
        nullable: fieldSchema.nullable,
        isGroup: true,
        groupFields: expandSchemaToFields(fieldSchema, fullFieldName, depth + 1),
      };
      fields.push(groupField);
      return;
    }

    // Expand inline for nested objects
    if (shouldExpandInline(fieldSchema) && parentPath) {
      fields.push(...expandSchemaToFields(fieldSchema, fullFieldName, depth + 1));
      return;
    }

    // Create regular field
    const enumSchema = extractEnumSchema(fieldSchema);
    const field: ExpandedField = {
      name: fullFieldName,
      displayName: fieldName,
      dataType: enumSchema ? 'enum' : getSchemaDataType(fieldSchema),
      required: isRequired,
      description: fieldSchema.description,
      schema: fieldSchema,
      example: generateSchemaExample(fieldSchema, fieldName),
      defaultValue: fieldSchema.default,
      enum: enumSchema?.enum,
      enumNames: enumSchema?.['x-enumNames'],
      items: fieldSchema.items,
      nullable: fieldSchema.nullable,
      isNested: parentPath !== '',
      parentPath: parentPath || undefined,
    };

    fields.push(field);
  });

  return dedupeExpandedFields(fields);
}

/**
 * Check if a schema should create a group
 * @param schema - The schema to check
 * @returns Whether to create a group
 */
export function shouldCreateGroup(schema: Schema | undefined): boolean {
  if (!schema) return false;

  // Don't create group for oneOf with single enum schema
  if (isSingleEnumOneOf(schema)) {
    return false;
  }

  return !!(
    ((schema.type === 'object' && schema.properties && Object.keys(schema.properties).length > 0) ||
      schema.allOf ||
      schema.oneOf)
  );
}

/**
 * Check if a schema should be expanded inline
 * @param schema - The schema to check
 * @returns Whether to expand inline
 */
export function shouldExpandInline(schema: Schema | undefined): boolean {
  if (!schema || isSingleEnumOneOf(schema)) {
    return false;
  }

  return !!((schema.type === 'object' && schema.properties) || schema.allOf || schema.oneOf);
}

export function dedupeExpandedFields(fields: ExpandedField[]): ExpandedField[] {
  const deduped = new Map<string, ExpandedField>();

  fields.forEach(field => {
    const key = `${field.isGroup ? 'group' : 'field'}:${field.name}`;
    const existing = deduped.get(key);

    if (!existing) {
      deduped.set(key, field);
      return;
    }

    if (field.isGroup && existing.isGroup) {
      deduped.set(key, {
        ...existing,
        ...field,
        groupFields: dedupeExpandedFields([...(existing.groupFields ?? []), ...(field.groupFields ?? [])]),
      });
      return;
    }

    deduped.set(key, {
      ...field,
      ...existing,
      description: existing.description ?? field.description,
      example: existing.example ?? field.example,
      defaultValue: existing.defaultValue ?? field.defaultValue,
      enum: existing.enum ?? field.enum,
      enumNames: existing.enumNames ?? field.enumNames,
    });
  });

  return Array.from(deduped.values());
}
